import { useState, useEffect, useCallback } from "react";
import { DateTime } from "luxon";
import { formatZoneFromISO } from "../../utils/timezoneLabel";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ticketApi } from "../../api/ticket";
import { accountApi } from "../../api/account";
import { paymentApi } from "../../api/payment";
import {
    Elements,
    PaymentElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import Icon from "@mdi/react";
import { mdiCheckCircle, mdiCheck, mdiClockOutline } from "@mdi/js";

import BookingOptionModal from "../../components/BookingOptionModal";
import GuestLoginModal from "../../components/GuestLoginModal";
import GuestAccountOptIn from "../../components/GuestAccountOptIn";
import useGuestAccountCreation from "../../hooks/useGuestAccountCreation";
import CustomFormInput from "../../components/forms/CustomFormInput";
import SupplierPolicyAcknowledgement from "../../components/SupplierPolicyAcknowledgement";
import { authApi } from "../../api/auth";
import { formatCurrencyFromMinor } from "../../utils/currency";
import { isOrgPaymentsNotReady, orgPaymentsNotReadyMessage } from "../../utils/orgPayments";
import { isOrgDomainClosed } from "../../utils/orgDomainClosed";
import "../../style.css"; /* Global CSS variables - ensure they load first */
import "../header.css"; /* Includes Common.css via @import */
import "./TicketPurchase.css";
import StoreBanner from "../../components/store-banner";
import { useDevMode } from "../../hooks/useDevMode";
import { getStripePromise } from "../../utils/stripeKey";
import ServiceFeeInfo from "../../components/ServiceFeeInfo";


function TicketPaymentForm({ transactionId, onSuccess, policyAck, payLabel }) {
    const stripe = useStripe();
    const elements = useElements();
    const [isProcessing, setIsProcessing] = useState(false);
    const [elementsReady, setElementsReady] = useState(false);
    const [paymentError, setPaymentError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!stripe || !elements || !elementsReady) return;
        if (!policyAck) return;

        setIsProcessing(true);
        setPaymentError("");

        try {
            const { error, paymentIntent } = await stripe.confirmPayment({
                elements,
                redirect: "if_required",
            });

            if (error) {
                // Shown inline below the PaymentElement (see paymentError render). Do NOT
                // also bubble to the parent's purchaseError — that duplicates the same
                // message above and below the Pay Now button.
                setPaymentError(error.message || "Payment failed. Please try again.");
                setIsProcessing(false);
                return;
            }

            if (paymentIntent?.status === "succeeded") {
                onSuccess(transactionId);
            } else {
                setPaymentError("Payment not completed. Please try again.");
                setIsProcessing(false);
            }
        } catch (err) {
            setPaymentError(err.message || "Payment failed. Please try again.");
            setIsProcessing(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="ticket-stripe-form">

            <PaymentElement onReady={() => setElementsReady(true)} onLoadError={(e) => setPaymentError(e?.error?.message || "Could not load the payment form. Please try again.")} options={{ layout: 'tabs', paymentMethodOrder: ['apple_pay', 'google_pay', 'card'] }} />
            {paymentError && (
                <div className="purchase-error-message" style={{ marginTop: '12px' }}>
                    {paymentError}
                </div>
            )}
            <button
                type="submit"
                className="btn-purchase"
                disabled={!stripe || !elements || !elementsReady || isProcessing || !policyAck}
                style={{ marginTop: '16px', width: '100%' }}
            >
                {isProcessing
                    ? "Processing..."
                    : !elementsReady
                        ? "Loading payment..."
                        : !policyAck
                            ? "Agree to the terms to continue"
                            : payLabel || "Pay"}
            </button>
        </form>
    );
}

/**
 * TicketPurchase - Web-based ticket purchasing page with mobile integration
 * 
 * MOBILE INTEGRATION:
 * When accessed via mobile redirect (/tickets?eventId=XXX&returnApp=mobile),
 * displays a visual countdown timer for psychological urgency. Timer is purely
 * for UX enhancement and doesn't enforce any purchase deadlines.
 * 
 * TIMER BEHAVIOR:
 * - 3-minute countdown for mobile users only
 * - Red color with pulsing animation (faster when <1min)
 * - Disappears when reaching zero (no purchase blocking)
 * - Independent of JWT token expiration (1hr + 30 days)
 * 
 * @see /Users/bradkile/Documents/Apps/projects/vc/dance-locale-web/CLAUDE.md - Future timer enhancements
 */

// Event times come from the backend as UTC ISO. Render them in the event's timezone (derived
// from the organiser's location); fall back to the buyer's browser zone when none is set.
const formatEventDate = (iso, timezone) => {
    if (!iso) return 'Date TBA';
    let dt = DateTime.fromISO(iso, { zone: 'utc' });
    if (!dt.isValid) return 'Date TBA';
    dt = timezone ? dt.setZone(timezone) : dt.toLocal();
    return dt.toFormat('cccc, dd LLLL yyyy');
};

// Event time as a start-end range in the event tz, indicator shown once — e.g.
// "6:00 PM - 10:00 PM (GMT+8 Perth)". A multi-day event appends the end date so the range
// stays unambiguous. Falls back to start-only when no end time is present.
const formatEventTimeRange = (startIso, endIso, timezone) => {
    if (!startIso) return '';
    const toDt = (iso) => {
        const d = DateTime.fromISO(iso, { zone: 'utc' });
        return timezone ? d.setZone(timezone) : d.toLocal();
    };
    const s = toDt(startIso);
    if (!s.isValid) return '';
    const label = timezone ? formatZoneFromISO(timezone, startIso) : s.toFormat('ZZZZ');
    if (!endIso) return `${s.toFormat('h:mm a')} (${label})`;
    const e = toDt(endIso);
    const range = (e.isValid && s.hasSame(e, 'day'))
        ? `${s.toFormat('h:mm a')} - ${e.toFormat('h:mm a')}`
        : `${s.toFormat('h:mm a')} - ${e.toFormat("ccc d LLL, h:mm a")}`;
    return `${range} (${label})`;
};

// Guest ticket-buyer details, cached for the life of the tab so a Stripe error or a
// detour out of the page does not cost the user their typing. Versioned suffix so a
// future shape change cannot read a stale object.
const GUEST_FORM_STORAGE_KEY = "ticketPurchase.guestForm.v1";

const TicketPurchase = () => {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    useDevMode();

    // URL params
    const [, setEventId] = useState("");
    const [returnApp, setReturnApp] = useState("");
    // Where Cancel should send a web user back to (the event landing page they came
    // from). Only honoured if it resolves to a dancelocale.com URL — see handleCancel.
    const [returnUrl, setReturnUrl] = useState("");
    
    // Event data
    const [eventData, setEventData] = useState(null);
    const [orgName, setOrgName] = useState('');
    const [policyAck, setPolicyAck] = useState(false);
    const [brandingData, setBrandingData] = useState(null);

    // Authenticated user's name (fetched from profile)
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [userDisplayName, setUserDisplayName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    // Heading for the full-page error state; set when the organiser has switched ticketing off.
    const [errorTitle, setErrorTitle] = useState("");

    // Guest info state. First/last are separate fields (matching the booking
    // guest form) so the register payload gets clean names — no splitting a
    // single string, which is where the "Brad Brad" duplication came from.
    // Persist guest details across navigation (e.g. a Stripe error → Go Back, or
    // bouncing out to check an email) so they are not retyped. Mirrors the
    // booking flow's FORM_STORAGE_KEY pattern in BookingConfirmation.js.
    // sessionStorage, not localStorage: it clears on tab close, which is the
    // right lifetime for someone else's personal details on a shared device.
    const persistedGuest = (() => {
        try {
            return JSON.parse(sessionStorage.getItem(GUEST_FORM_STORAGE_KEY) || "{}");
        } catch {
            return {};
        }
    })();

    const [guestFirstName, setGuestFirstName] = useState(persistedGuest.firstName || "");
    const [guestLastName, setGuestLastName] = useState(persistedGuest.lastName || "");
    const [guestEmail, setGuestEmail] = useState(persistedGuest.email || "");
    const [guestPhone, setGuestPhone] = useState(persistedGuest.phone || "");
    const [guestFormErrors, setGuestFormErrors] = useState({});

    // Cache guest details as they type. Guests ONLY — a logged-in buyer is prefilled
    // from their profile, so caching theirs would leak their name and email to the
    // next guest using the same tab (the privacy rule BookingConfirmation.js follows).
    useEffect(() => {
        if (isAuthenticated) return;
        try {
            sessionStorage.setItem(
                GUEST_FORM_STORAGE_KEY,
                JSON.stringify({
                    firstName: guestFirstName,
                    lastName: guestLastName,
                    email: guestEmail,
                    phone: guestPhone,
                })
            );
        } catch {
            // sessionStorage can be unavailable (private mode) — not worth failing over
        }
    }, [isAuthenticated, guestFirstName, guestLastName, guestEmail, guestPhone]);
    const [showCreateAccountCard, setShowCreateAccountCard] = useState(false);
    const [showGuestLoginModal, setShowGuestLoginModal] = useState(false);

    // Inline "create a free account" opt-in (shown only once we know the email
    // has no existing account — a known-existing email routes to GuestLoginModal
    // instead). When checked, a password field appears and, on submit, we
    // register + log in BEFORE creating the payment session so the purchase
    // completes as an authenticated user.
    const [emailIsNew, setEmailIsNew] = useState(false);
    // Known-account emails must log in to buy — the ecosystem is mobile-as-assistant,
    // so a ticket bought as a guest with an account's own email would land outside
    // the app wallet, defeating the point. We block guest checkout for these.
    const [emailHasAccount, setEmailHasAccount] = useState(false);

    // Inline "create a free account" opt-in (shared with the booking flow). The
    // hook owns wantsAccount/password + the register→login sequence; email gating
    // (emailIsNew/emailHasAccount) stays here as it's ticket-flow specific.
    const guestAccount = useGuestAccountCreation();

    const handleGuestEmailBlur = useCallback(async (emailValue) => {
        if (!emailValue || !emailValue.includes("@") || !emailValue.includes(".")) {
            setEmailIsNew(false);
            setEmailHasAccount(false);
            return;
        }
        try {
            const { hasAccount } = await authApi.checkEmail(emailValue);
            if (hasAccount) {
                // Existing account — require login, never the create-account opt-in,
                // and never a guest checkout with this email.
                setEmailIsNew(false);
                guestAccount.setWantsAccount(false);
                setEmailHasAccount(true);
                setShowGuestLoginModal(true);
            } else {
                setEmailIsNew(true);
                setEmailHasAccount(false);
            }
        } catch (_) {
            // Silently ignore — don't block the form if the check fails. Leave the
            // opt-in hidden rather than risk offering account creation for an email
            // that actually exists.
            setEmailIsNew(false);
            setEmailHasAccount(false);
        }
    }, [guestAccount]);

    // Purchase data
    const [ticketSelections, setTicketSelections] = useState({});
    const [totalAmount, setTotalAmount] = useState(0);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

    // Purchase flow state
    const [isProcessing, setPurchaseProcessing] = useState(false);
    const [purchaseError, setPurchaseError] = useState("");
    const [purchaseSuccess, setPurchaseSuccess] = useState(false);

    // Stripe payment state
    const [stripeClientSecret, setStripeClientSecret] = useState(null);
    const [stripeTransactionId, setStripeTransactionId] = useState(null);
    const [showStripeForm, setShowStripeForm] = useState(false);
    const [isCreatingPaymentSession, setIsCreatingPaymentSession] = useState(false);
    const [paymentAmounts, setPaymentAmounts] = useState(null); // fee breakdown from backend
    // Server quote for the current selection, shown BEFORE Purchase so the buyer sees the
    // Service fee and the full total up front (never only after the click).
    const [quote, setQuote] = useState(null);
    const [isQuoting, setIsQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState("");

    // Urgency timer state (for mobile users only)
    const [timeRemaining, setTimeRemaining] = useState(180); // 3 minutes in seconds
    const showTimer = returnApp === "mobile" && timeRemaining > 0;

    // Guest purchase option modal — web vs app choice (like booking flow)
    const [showPurchaseOptionModal, setShowPurchaseOptionModal] = useState(false);
    const [hasChosenWebPurchase, setHasChosenWebPurchase] = useState(false);
    const [showDownloadBanner, setShowDownloadBanner] = useState(false);
    const [pendingPurchaseEventId, setPendingPurchaseEventId] = useState(null);

    // Demo mode — uses hardcoded data, no API calls or real Stripe
    const [isDemo, setIsDemo] = useState(false);

    useEffect(() => {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const eventIdParam = urlParams.get("eventId");
        const returnAppParam = urlParams.get("returnApp");
        const returnUrlParam = urlParams.get("returnUrl");
        const devModeParam = urlParams.get("devMode");

        // Development mode auto-detected via URL parameter (handled by client.js interceptor)
        if (devModeParam === "true") {
            console.log("🧪 TicketPurchase: Dev mode URL parameter detected (routing handled automatically)");
        }

        if (!eventIdParam) {
            // No eventId: /tickets is purchase-only — discovery lives at /events
            navigate("/events", { replace: true });
            return;
        }

        // EventId provided - direct purchase mode
        setEventId(eventIdParam);
        setReturnApp(returnAppParam || "");
        setReturnUrl(returnUrlParam || "");

        loadEventData(eventIdParam);
    }, []);

    useEffect(() => {
        // Calculate total amount when selections change
        calculateTotal();
    }, [ticketSelections, eventData]);

    // Urgency timer countdown (for mobile users only)
    useEffect(() => {
        if (showTimer) {
            const interval = setInterval(() => {
                setTimeRemaining(prev => Math.max(0, prev - 1));
            }, 1000);
            
            return () => clearInterval(interval);
        }
    }, [showTimer]);

    // Fetch profile name as soon as auth is confirmed — runs after AuthContext settles
    useEffect(() => {
        if (!isAuthenticated) return;
        accountApi.getProfile()
            .then(profile => {
                setUserFirstName(profile.firstName || '');
                setUserLastName(profile.lastName || '');
                setUserDisplayName(profile.username || '');
            })
            .catch(() => {});
    }, [isAuthenticated]);

    // Apply school branding CSS vars after React renders the root element
    useEffect(() => {
        if (!brandingData) return;
        const root = document.getElementById('ticket-purchase-root');
        if (!root) return;
        if (brandingData.buttonColor)     root.style.setProperty('--school-btn-color', brandingData.buttonColor);
        if (brandingData.buttonTextColor) root.style.setProperty('--school-btn-text', brandingData.buttonTextColor);
        if (brandingData.headerColor)     root.style.setProperty('--school-header-color', brandingData.headerColor);
        if (brandingData.headerTextColor) root.style.setProperty('--school-header-text', brandingData.headerTextColor);
        if (brandingData.backgroundColor) root.style.setProperty('--school-bg-color', brandingData.backgroundColor);
    }, [brandingData, loading]);

    // Show web-vs-app choice for users arriving via direct URL (not mobile redirect)
    useEffect(() => {
        if (eventData && returnApp !== 'mobile' && !hasChosenWebPurchase) {
            setShowPurchaseOptionModal(true);
        }
    }, [eventData, returnApp, hasChosenWebPurchase]);

    const handleOpenInApp = () => {
        setShowPurchaseOptionModal(false);
        const eventId = pendingPurchaseEventId || eventData?.event?.id;
        setPendingPurchaseEventId(null);

        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isAndroid = /Android/.test(navigator.userAgent);

        if (isIOS) {
            // Mobile: try deep link, fall back to App Store
            const deepLinkUrl = `dancelocale://tickets?eventId=${eventId}`;
            window.location.href = deepLinkUrl;
            setTimeout(() => {
                window.location.href = 'https://apps.apple.com/au/app/dance-local%C3%A9/id6748289759';
            }, 1500);
        } else if (isAndroid) {
            // Mobile: intent URL with Play Store fallback
            window.location.href = `intent://tickets?eventId=${eventId}#Intent;scheme=dancelocale;package=com.yadawn.dancelocale;S.browser_fallback_url=https://play.google.com/store/apps/details?id=com.yadawn.dancelocale;end`;
        } else {
            // Desktop: show download suggestion
            setShowDownloadBanner(true);
        }
    };

    const handleContinueOnWeb = () => {
        setShowPurchaseOptionModal(false);
        setHasChosenWebPurchase(true);
        if (pendingPurchaseEventId) {
            startPurchaseFlow(pendingPurchaseEventId);
            setPendingPurchaseEventId(null);
        }
    };

    // Format time remaining as MM:SS
    const formatTimeRemaining = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const DEMO_EVENT_DATA = {
        event: {
            title: 'Latin Night — Salsa & Bachata',
            description: 'An evening of social dancing with live DJ. All levels welcome.',
            startDateTime: '2026-08-09T19:00:00',
            location: { description: 'The Venue', suburb: 'South Brisbane', state: 'QLD', postcode: '4101' },
        },
        ticketTypes: [
            { name: 'General Admission', description: 'General entry', price: 2500, available: 45 },
            { name: 'VIP', description: 'Priority entry + welcome drink', price: 4500, available: 8 },
        ],
        paymentMethods: ['stripe'],
        organisationId: 'demo',
        ticketingConfigId: 'demo',
        orgName: 'Latin Fusion Dance Studio',
        branding: {
            headerColor: '#ffb1e6',
            headerTextColor: '#4a0a3a',
            buttonColor: '#ffb1e6',
            buttonTextColor: '#4a0a3a',
            backgroundColor: '#fff5fc',
        },
    };

    const loadEventData = async (eventId) => {
        if (eventId === 'demo') {
            setIsDemo(true);
            setEventData(DEMO_EVENT_DATA);
            setOrgName(DEMO_EVENT_DATA.orgName);
            setBrandingData(DEMO_EVENT_DATA.branding);
            setUserFirstName('Sarah');
            setUserLastName('Mitchell');
            const initialSelections = {};
            DEMO_EVENT_DATA.ticketTypes.forEach((t, i) => { initialSelections[t.name] = i === 0 ? 1 : 0; });
            setTicketSelections(initialSelections);
            setSelectedPaymentMethod('stripe');
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const data = await ticketApi.getEventForPurchase(eventId);

            if (data.success) {
                setEventData(data.availableTickets);
                setOrgName(data.availableTickets?.orgName || '');

                // Store branding — applied via useEffect after React renders the root element
                setBrandingData(data.availableTickets?.branding || null);

                // Fetch authenticated user's name for the sanity-check display

                // Initialize ticket selections — pre-select 1 of the first in-stock
                // ticket type (most buyers purchase a single ticket)
                const initialSelections = {};
                let preselected = false;
                data.availableTickets.ticketTypes.forEach(ticketType => {
                    const inStock = ticketType.availableQuantity === undefined || ticketType.availableQuantity > 0;
                    if (!preselected && inStock) {
                        initialSelections[ticketType.name] = 1;
                        preselected = true;
                    } else {
                        initialSelections[ticketType.name] = 0;
                    }
                });
                setTicketSelections(initialSelections);

                // Set default payment method
                if (data.availableTickets.paymentMethods.length > 0) {
                    setSelectedPaymentMethod(data.availableTickets.paymentMethods[0]);
                }
            } else {
                setError("Failed to load event information");
            }
        } catch (error) {
            console.error("Error loading event data:", error);
            if (isOrgDomainClosed(error)) setErrorTitle("Tickets not sold online");
            setError(error.message || "Failed to load event");
        } finally {
            setLoading(false);
        }
    };

    const startPurchaseFlow = (eventId) => {
        setLoading(true);
        // Reset any previous purchase state
        setShowStripeForm(false);
        setStripeClientSecret(null);
        setPurchaseSuccess(false);
        setPurchaseError('');
        loadEventData(eventId);
    };

    // The selections payload the backend prices (same shape for the quote and the purchase).
    const buildSelections = () => {
        const selections = [];
        Object.entries(ticketSelections).forEach(([ticketType, quantity]) => {
            if (quantity > 0) {
                const ticketInfo = eventData.ticketTypes.find(t => t.name === ticketType);
                selections.push({
                    ticketType,
                    quantity,
                    price: ticketInfo.price,
                    currency: ticketInfo.currency
                });
            }
        });
        return selections;
    };

    // Re-quote whenever the selection changes (debounced; stale responses ignored).
    useEffect(() => {
        if (!eventData?.ticketingConfigId || isDemo) return undefined;
        const selections = buildSelections();
        if (selections.length === 0) {
            setQuote(null);
            setQuoteError("");
            setIsQuoting(false);
            return undefined;
        }
        let cancelled = false;
        setIsQuoting(true);
        const timer = setTimeout(async () => {
            try {
                const fees = await paymentApi.quoteTicketCharge({
                    sourceId: eventData.ticketingConfigId,
                    ticketSelections: selections,
                });
                if (!cancelled) { setQuote(fees); setQuoteError(""); }
            } catch (err) {
                console.error("Ticket quote failed:", err);
                if (!cancelled) {
                    setQuote(null);
                    setQuoteError(err.message || "These tickets can't be priced right now. Please try again.");
                }
            } finally {
                if (!cancelled) setIsQuoting(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketSelections, eventData, isDemo]);

    // Fees to display: the live payment session's once it exists, else the quote.
    const displayFees = paymentAmounts || quote;

    const calculateTotal = () => {
        if (!eventData) return;

        let total = 0;
        Object.entries(ticketSelections).forEach(([ticketType, quantity]) => {
            const ticketInfo = eventData.ticketTypes.find(t => t.name === ticketType);
            if (ticketInfo && quantity > 0) {
                total += ticketInfo.price * quantity;
            }
        });
        setTotalAmount(total);
    };

    const updateTicketQuantity = (ticketType, quantity) => {
        const ticketInfo = eventData.ticketTypes.find(t => t.name === ticketType);
        const maxQuantity = Math.min(ticketInfo.availableQuantity, 10); // Max 10 per purchase
        
        const validQuantity = Math.max(0, Math.min(quantity, maxQuantity));
        
        setTicketSelections(prev => ({
            ...prev,
            [ticketType]: validQuantity
        }));
    };

    const validateGuestForm = () => {
        const errors = {};
        if (!guestFirstName.trim()) {
            errors.name = "First name is required";
        }
        if (!guestLastName.trim()) {
            errors.lastName = "Last name is required";
        }
        if (!guestEmail.trim()) {
            errors.email = "Email is required to receive your ticket";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
            errors.email = "Please enter a valid email address";
        }
        setGuestFormErrors(errors);
        // Password (opt-in) validity is owned by the shared hook.
        const passwordOk = guestAccount.validatePassword();
        return Object.keys(errors).length === 0 && passwordOk;
    };

    const handlePurchase = async () => {
        // Policy acknowledgement gate — buyer must agree before advancing to payment.
        if (!policyAck) return;
        // Known-account email must log in — no guest checkout (ticket must land in
        // their app wallet, not just an emailed QR). Re-surface the login modal.
        if (!isAuthenticated && emailHasAccount) {
            setShowGuestLoginModal(true);
            return;
        }
        // Guests must fill out the guest form
        if (!isAuthenticated && !validateGuestForm()) {
            return;
        }

        setPurchaseProcessing(true);
        setPurchaseError("");

        // If the guest opted into creating an account, register + log in first so
        // the purchase completes as an authenticated user (their ticket lands in
        // the account automatically). isAuthenticated won't flip synchronously
        // this render, so we track success locally and take the authed path.
        let purchaseAsAuthed = isAuthenticated;
        if (!isAuthenticated && guestAccount.wantsAccount) {
            // The opt-in shows by default, so the user may have checked it without
            // ever triggering the on-blur email check. Confirm the email is actually
            // new before creating an account — an existing email must route to login,
            // never a duplicate-account attempt.
            let emailConfirmedNew = emailIsNew;
            if (!emailConfirmedNew) {
                try {
                    const { hasAccount } = await authApi.checkEmail(guestEmail.trim());
                    if (hasAccount) {
                        setEmailHasAccount(true);
                        guestAccount.setWantsAccount(false);
                        guestAccount.setPassword("");
                        setShowGuestLoginModal(true);
                        setPurchaseProcessing(false);
                        return;
                    }
                    setEmailIsNew(true);
                    emailConfirmedNew = true;
                } catch (_) {
                    // Can't confirm — fail safe: don't create an account, surface an error.
                    guestAccount.setPasswordError("Could not verify your email. Please try again.");
                    setPurchaseProcessing(false);
                    return;
                }
            }

            const created = await guestAccount.createAccountIfRequested({
                firstName: guestFirstName,
                lastName: guestLastName,
                email: guestEmail,
            });
            if (!created) {
                setPurchaseProcessing(false);
                return; // hook set its own password error
            }
            purchaseAsAuthed = true;
        }

        try {
            // Prepare ticket selections for API
            const selections = buildSelections();

            if (selections.length === 0) {
                setPurchaseError("Please select at least one ticket");
                setPurchaseProcessing(false);
                return;
            }

            // For authenticated users (incl. a guest who just created an account):
            // create Stripe payment session first
            if (purchaseAsAuthed) {
                try {
                    setIsCreatingPaymentSession(true);
                    const paymentData = await paymentApi.createPaymentIntent({
                        sourceType: "ticket",
                        sourceId: eventData.ticketingConfigId,
                        organisationId: eventData.event.organisationId,
                        // Server re-prices from these selections; basePrice is no longer trusted.
                        ticketSelections: selections,
                        currency: eventData.ticketTypes[0]?.currency,
                    });

                    setStripeClientSecret(paymentData.paymentIntent.clientSecret);
                    setStripeTransactionId(paymentData.transactionId);
                    if (paymentData.amounts) setPaymentAmounts(paymentData.amounts);
                    setShowStripeForm(true);
                    setPurchaseProcessing(false);
                } catch (err) {
                    console.error("Payment session creation error:", err);
                    setPurchaseError(isOrgPaymentsNotReady(err)
                        ? orgPaymentsNotReadyMessage("The event organiser", "them")
                        : (err.message || "Failed to initiate payment. Please try again."));
                    setPurchaseProcessing(false);
                } finally {
                    setIsCreatingPaymentSession(false);
                }
                return; // Don't fall through to purchase API call — TicketPaymentForm handles next step
            }

            // Guest path: create real Stripe payment session then show payment form
            try {
                setIsCreatingPaymentSession(true);
                const paymentData = await paymentApi.createGuestPaymentIntent({
                    sourceType: "ticket",
                    sourceId: eventData.ticketingConfigId,
                    organisationId: eventData.event.organisationId,
                    // Server re-prices from these selections; basePrice is no longer trusted.
                    ticketSelections: selections,
                    currency: eventData.ticketTypes[0]?.currency,
                    guestEmail: guestEmail.trim(),
                    // Stored on the transaction so the webhook can issue + email tickets
                    // even if the browser never returns after Stripe success.
                    guestInfo: {
                        name: `${guestFirstName.trim()} ${guestLastName.trim()}`.trim(),
                        email: guestEmail.trim(),
                        phone: guestPhone.trim(),
                    },
                });

                setStripeClientSecret(paymentData.paymentIntent.clientSecret);
                setStripeTransactionId(paymentData.transactionId);
                if (paymentData.amounts) setPaymentAmounts(paymentData.amounts);
                setShowStripeForm(true);
                setPurchaseProcessing(false);
            } catch (err) {
                console.error("Guest payment session creation error:", err);
                setPurchaseError(isOrgPaymentsNotReady(err)
                    ? orgPaymentsNotReadyMessage("The event organiser", "them")
                    : (err.message || "Failed to initiate payment. Please try again."));
                setPurchaseProcessing(false);
            } finally {
                setIsCreatingPaymentSession(false);
            }
            return; // TicketPaymentForm handles next step via handleStripeSuccess

        } catch (error) {
            console.error("Purchase error:", error);
            // Existing-account safety net. NOTE: the compat client throws a plain Error with no
            // .response/.code, so this 409 branch only matches if the thrown message names the code;
            // the onBlur email check is the primary ACCOUNT_EXISTS guard. Message lives in error.message.
            if (error.response?.status === 409 || error.response?.data?.code === "ACCOUNT_EXISTS" || /account.*exists/i.test(error.message || "")) {
                setShowGuestLoginModal(true);
            } else {
                setPurchaseError(error.message || "Purchase failed. Please try again.");
            }
        } finally {
            setPurchaseProcessing(false);
        }
    };

    const handleStripeSuccess = useCallback(async (transactionId) => {
        try {
            setPurchaseProcessing(true);
            const selections = [];
            Object.entries(ticketSelections).forEach(([ticketType, quantity]) => {
                if (quantity > 0) {
                    const ticketInfo = eventData.ticketTypes.find(t => t.name === ticketType);
                    selections.push({ ticketType, quantity, price: ticketInfo.price, currency: ticketInfo.currency });
                }
            });

            const purchasePayload = {
                ticketingConfigId: eventData.ticketingConfigId,
                ticketSelections: selections,
                paymentData: {
                    paymentMethod: "stripe",
                    transactionId,
                    paymentStatus: "completed",
                },
            };

            if (!isAuthenticated) {
                purchasePayload.guestInfo = {
                    name: `${guestFirstName.trim()} ${guestLastName.trim()}`.trim(),
                    email: guestEmail.trim(),
                    phone: guestPhone.trim(),
                };
            }

            const response = await ticketApi.purchaseTicket(purchasePayload);

            if (response.success) {
                setShowStripeForm(false);
                setPurchaseSuccess(true);
                // Purchase completed: the cached details have served their purpose and
                // must not persist for whoever uses this tab next.
                try { sessionStorage.removeItem(GUEST_FORM_STORAGE_KEY); } catch { /* ignore */ }
                if (!isAuthenticated) {
                    setShowCreateAccountCard(true);
                }
                // No mobile auto-redirect: the purchase page runs inside the app's in-app
                // browser (WebBrowser.openBrowserAsync), which blocks a dancelocale:// deep
                // link — only the browser's native Done/✓ can dismiss it. The success screen
                // tells the mobile buyer to tap Done; we don't show a redirect countdown that
                // can't fire.
            } else {
                setPurchaseError(response.message || "Purchase failed after payment. Contact support.");
            }
        } catch (err) {
            console.error("Post-payment purchase error:", err);
            setPurchaseError("Payment was taken but ticket confirmation failed. Please contact support with your payment reference.");
        } finally {
            setPurchaseProcessing(false);
        }
    }, [ticketSelections, eventData, returnApp, isAuthenticated, guestFirstName, guestLastName, guestEmail, guestPhone]);

    const handleCancel = () => {
        if (returnApp === "mobile") {
            window.location.href = "dancelocale://tickets/cancelled";
            return;
        }
        // Return the user to the event landing page they came from, if one was passed.
        // Guard against open-redirects AND unreachable hosts: only honour a returnUrl
        // on dancelocale.com OR on the SAME origin as this page. The same-origin check
        // is what makes localhost safe — a returnUrl at localhost:9000 (a dead backend
        // port) while we're on localhost:3000 is rejected and we fall back to /tickets,
        // instead of redirecting to a port that isn't running.
        if (returnUrl) {
            try {
                const url = new URL(returnUrl);
                const allowed =
                    url.hostname === "dancelocale.com" ||
                    url.hostname.endsWith(".dancelocale.com") ||
                    url.origin === window.location.origin;
                if (allowed) {
                    window.location.href = returnUrl;
                    return;
                }
            } catch {
                /* malformed returnUrl — fall through to the calendar */
            }
        }
        // No mobile deep-link and no external returnUrl → the buyer came from the
        // in-app Find Events calendar, so return them there rather than Home.
        navigate("/tickets");
    };

    if (loading) {
        return (
            <div className="ticket-purchase-container">
                <div className="loading-container">
                    <div className="loading-text">Loading event information...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="ticket-purchase-container">
                <div className="loading-container">
                    <div className={`error-message${errorTitle ? " error-message--info" : ""}`}>
                        <h2>{errorTitle || "Error"}</h2>
                        <p>{error}</p>
                        {/* On mobile, the in-app browser's native ✓ returns to the app; the in-page
                            deep-link "Go Back" is a no-op there, so hide it. */}
                        {returnApp !== "mobile" && (
                            <button
                                onClick={handleCancel}
                                className="btn-purple"
                            >
                                Go Back
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (purchaseSuccess) {
        return (
            <div className="ticket-purchase-container">
                <div className="success-screen">
                    <div className="success-screen-container">
                    <div className="success-content">
                        <Icon path={mdiCheckCircle} size={2} color="var(--school-btn-color, var(--dl-color-purple-default))" style={{ marginBottom: '6px' }} />
                        <h2 className="success-title">Purchase Successful!</h2>
                        <p className="success-message">
                            Your tickets have been purchased successfully.
                            {isAuthenticated
                                ? " Open the app and scan your QR code at the door. A copy has also been sent to your email as a backup."
                                : " Your QR code ticket has been sent to " + guestEmail + ". Show it at the door to check in."}
                        </p>
                        {returnApp === "mobile" && (
                            <p className="redirect-message">
                                Tap Done at the top of this window to return to the app.
                            </p>
                        )}
                        {returnApp !== "mobile" && isAuthenticated && (
                            <button
                                className="btn-purchase"
                                onClick={() => navigate("/my-hub")}
                                style={{ marginTop: '12px', width: '100%' }}
                            >
                                View My Tickets
                            </button>
                        )}
                        {returnApp !== "mobile" && (
                            <button
                                type="button"
                                className="success-home-link success-home-link-btn"
                                onClick={() => navigate("/")}
                            >
                                Go to home page
                            </button>
                        )}
                    </div>

                    {/* Create Account CTA — shown to guests after purchase, INCLUDING the
                        mobile handoff. Registering on the web inside the app's browser is
                        awkward, but a guest's tickets are not visible in the app at all
                        (my-tickets is authenticated), so this is their only route to an
                        account and removing it would just cost the signup. */}
                    {showCreateAccountCard && (
                        <div className="create-account-card">
                            <h3 className="create-account-card-title">
                                Want to manage your tickets?
                            </h3>
                            <ul className="create-account-card-list">
                                <li><Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" style={{ marginRight: '6px', verticalAlign: 'middle' }} />Book dance instructors directly in the app</li>
                                <li><Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" style={{ marginRight: '6px', verticalAlign: 'middle' }} />Track all your tickets in one place</li>
                                <li><Icon path={mdiCheck} size={0.7} color="var(--dl-color-green-default)" style={{ marginRight: '6px', verticalAlign: 'middle' }} />Discover events near you</li>
                            </ul>
                            <a
                                href={`/register${guestEmail ? `?email=${encodeURIComponent(guestEmail)}` : ""}`}
                                className="create-account-card-btn"
                            >
                                Create Account
                            </a>
                            <button
                                onClick={() => setShowCreateAccountCard(false)}
                                className="create-account-card-dismiss"
                            >
                                Maybe later
                            </button>
                        </div>
                    )}

                    {/* App Download CTA — shown to guests AND to anyone who just created
                        an account via the inline opt-in (the account is what the app runs
                        on, so send them to the store). Not shown to users who were already
                        signed in before this purchase, and never on the mobile handoff:
                        that buyer is reading this inside the app's own browser, so telling
                        them to go and download it is nonsense. */}
                    {returnApp !== "mobile" && (!isAuthenticated || guestAccount.createdAccountThisSession) && (
                        <div className="app-download-card">
                            <p className="app-download-card-title">
                                {guestAccount.createdAccountThisSession ? "Your account is ready. Get the app" : "Want a better experience?"}
                            </p>
                            <p className="app-download-card-body">
                                Download the Dance Localé app to view your tickets, book instructors, and discover events.
                            </p>
                            <div className="app-download-card-badges">
                                <StoreBanner />
                            </div>
                        </div>
                    )}

                    </div>
                </div>
            </div>
        );
    }

    // Purchase rendering
    return (
        <div className="ticket-purchase-container ticket-purchase-mode" id="ticket-purchase-root">
            {/* Header — school-branded card top */}
            <header className="ticket-purchase-header">
                <h1 className="ticket-header-org-name">{orgName}</h1>
                <p className="ticket-header-subtitle">Ticket Purchase</p>
                {(userFirstName || userLastName || userDisplayName) && (
                    <p className="ticket-header-user-name">
                        For {(userFirstName || userLastName) ? `${userFirstName} ${userLastName}`.trim() : userDisplayName}
                    </p>
                )}
                <style>{`
                    @keyframes pulse {
                        0%, 100% {
                            opacity: 1;
                        }
                        50% {
                            opacity: 0.7;
                        }
                    }
                `}</style>
            </header>

            {/* Main Content — white card body */}
            <div className="ticket-purchase-body">
            <main className="ticket-purchase-main">
                {/* Session-limit warning — in the body (not the branded header, whose colour
                    may clash with the warning), above the event name, so the time limit is
                    unmistakable. Opaque warning red; pulses in the final minute. */}
                {showTimer && (
                    <div className="purchase-timer-banner" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        background: 'var(--dl-color-danger-50)',
                        color: 'var(--dl-color-warning-red)',
                        border: '1px solid var(--dl-color-danger-200)',
                        borderRadius: '8px',
                        padding: '12px 16px',
                        marginBottom: '1rem',
                        fontSize: '0.95rem', fontWeight: 700,
                        animation: timeRemaining < 60 ? 'pulse 1s infinite' : 'none'
                    }}>
                        <Icon path={mdiClockOutline} size={0.8} color="var(--dl-color-warning-red)" />
                        Complete purchase in {formatTimeRemaining(timeRemaining)}
                    </div>
                )}
                {eventData && (
                    <>
                        {/* Event Information */}
                        <div className="event-info-card">
                            <h2 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem', color: 'var(--dl-color-purple-default)'}}>{eventData.event.title}</h2>
                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', fontSize: '0.875rem'}}>
                                <div>
                                    <strong>Date:</strong> {formatEventDate(eventData.event.startDateTime, eventData.event.timezone)}
                                </div>
                                <div>
                                    <strong>Time:</strong> {formatEventTimeRange(eventData.event.startDateTime, eventData.event.endDateTime, eventData.event.timezone)}
                                </div>
                                <div className="md:col-span-2">
                                    <strong>Location:</strong> {eventData.event.location?.description}
                                </div>
                            </div>
                        </div>

                        {/* Ticket Selection */}
                        <div className="ticket-selection-card">
                            <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--dl-color-purple-default)'}}>Select Tickets</h3>
                            <div>
                                {eventData.ticketTypes.map(ticketType => (
                                    <div key={ticketType.name} className="ticket-type-row">
                                        <div className="ticket-type-info">
                                            <h4 className="ticket-type-name">{ticketType.name}</h4>
                                            {ticketType.description && (
                                                <p className="ticket-type-description">{ticketType.description}</p>
                                            )}
                                            <p className="ticket-type-price">
                                                {formatCurrencyFromMinor(ticketType.price, ticketType.currency)}
                                            </p>
                                            <p className="ticket-type-availability">
                                                {ticketType.availableQuantity} available
                                            </p>
                                        </div>
                                        <div className="quantity-controls">
                                            <button
                                                onClick={() => updateTicketQuantity(ticketType.name, ticketSelections[ticketType.name] - 1)}
                                                disabled={ticketSelections[ticketType.name] <= 0}
                                                className="quantity-button"
                                            >
                                                -
                                            </button>
                                            <span className="quantity-display">
                                                {ticketSelections[ticketType.name] || 0}
                                            </span>
                                            <button
                                                onClick={() => updateTicketQuantity(ticketType.name, ticketSelections[ticketType.name] + 1)}
                                                disabled={ticketSelections[ticketType.name] >= ticketType.availableQuantity || ticketSelections[ticketType.name] >= 10}
                                                className="quantity-button"
                                            >
                                                +
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Authenticated buyer info — shown when user is logged in */}
                        {isAuthenticated && (
                            <div className="ticket-selection-card buyer-info-card">
                                <p className="buyer-info-label">Buying as</p>
                                <p className="buyer-info-name">
                                    {(userFirstName || userLastName)
                                        ? `${userFirstName} ${userLastName}`.trim()
                                        : (userDisplayName || user?.email)}
                                </p>
                                {(userFirstName || userLastName || userDisplayName) && user?.email && (
                                    <p className="buyer-info-email">{user.email}</p>
                                )}
                            </div>
                        )}

                        {/* Guest Info Form — shown when user is not logged in */}
                        {!isAuthenticated && (
                            <div className="ticket-selection-card">
                                <h3 style={{ fontSize: "1.25rem", fontWeight: "600", marginBottom: "1rem", color: "var(--dl-color-purple-default)" }}>Your Details</h3>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                    <div>
                                        <label style={{ display: "block", fontWeight: "600", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                                            First name <span style={{ color: "var(--dl-color-danger)" }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={guestFirstName}
                                            onChange={(e) => setGuestFirstName(e.target.value)}
                                            placeholder="First name"
                                            autoComplete="given-name"
                                            className="form-input"
                                            style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", border: guestFormErrors.name ? "1px solid var(--dl-color-danger)" : "1px solid var(--dl-color-lightplus)", fontSize: "0.875rem" }}
                                        />
                                        {guestFormErrors.name && (
                                            <p style={{ color: "var(--dl-color-danger)", fontSize: "0.75rem", marginTop: "0.25rem" }}>{guestFormErrors.name}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ display: "block", fontWeight: "600", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                                            Last name <span style={{ color: "var(--dl-color-danger)" }}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={guestLastName}
                                            onChange={(e) => setGuestLastName(e.target.value)}
                                            placeholder="Last name"
                                            autoComplete="family-name"
                                            className="form-input"
                                            style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", border: guestFormErrors.lastName ? "1px solid var(--dl-color-danger)" : "1px solid var(--dl-color-lightplus)", fontSize: "0.875rem" }}
                                        />
                                        {guestFormErrors.lastName && (
                                            <p style={{ color: "var(--dl-color-danger)", fontSize: "0.75rem", marginTop: "0.25rem" }}>{guestFormErrors.lastName}</p>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ display: "block", fontWeight: "600", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                                            Email <span style={{ color: "var(--dl-color-danger)" }}>*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={guestEmail}
                                            onChange={(e) => setGuestEmail(e.target.value)}
                                            onBlur={(e) => {
                                                setGuestEmail(e.target.value);
                                                handleGuestEmailBlur(e.target.value);
                                            }}
                                            placeholder="your@email.com"
                                            autoComplete="email"
                                            className="form-input"
                                            style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", border: guestFormErrors.email ? "1px solid var(--dl-color-danger)" : "1px solid var(--dl-color-lightplus)", fontSize: "0.875rem" }}
                                        />
                                        {guestFormErrors.email && (
                                            <p style={{ color: "var(--dl-color-danger)", fontSize: "0.75rem", marginTop: "0.25rem" }}>{guestFormErrors.email}</p>
                                        )}
                                        {emailHasAccount && (
                                            <p style={{ fontSize: "0.75rem", marginTop: "0.35rem", color: "var(--dl-color-purple-default)" }}>
                                                This email already has an account.{" "}
                                                <button
                                                    type="button"
                                                    onClick={() => setShowGuestLoginModal(true)}
                                                    style={{ background: "none", border: "none", padding: 0, color: "var(--dl-color-hyperlink)", textDecoration: "underline", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600 }}
                                                >
                                                    Log in
                                                </button>{" "}
                                                to buy so your ticket lands in your app.
                                            </p>
                                        )}
                                    </div>
                                    <div>
                                        <label style={{ display: "block", fontWeight: "600", marginBottom: "0.25rem", fontSize: "0.875rem" }}>
                                            Phone <span style={{ color: "var(--dl-color-text-muted)", fontWeight: "400" }}>(optional)</span>
                                        </label>
                                        <input
                                            type="tel"
                                            value={guestPhone}
                                            onChange={(e) => setGuestPhone(e.target.value)}
                                            onBlur={(e) => setGuestPhone(e.target.value)}
                                            placeholder="+1 (555) 000-0000"
                                            autoComplete="tel"
                                            className="form-input"
                                            style={{ width: "100%", padding: "0.5rem 0.75rem", borderRadius: "6px", border: "1px solid var(--dl-color-lightplus)", fontSize: "0.875rem" }}
                                        />
                                    </div>

                                    {/* Inline create-account opt-in (shared component). Shown BY DEFAULT
                                        so a user who never tabs out of the email field still sees it;
                                        hidden once the email is a known account (emailHasAccount gate),
                                        which routes to login instead. Unchecked by default so continuing
                                        as a guest stays the zero-friction path. The account unlocks the
                                        mobile app, so the copy leads with that. */}
                                    <GuestAccountOptIn
                                        visible={!emailHasAccount}
                                        label="Create a free account to use the Dance Localé app: your ticket, event reminders, and instructor chat, all in one place"
                                        checked={guestAccount.wantsAccount}
                                        onChange={(e) => {
                                            guestAccount.setWantsAccount(e.target.checked);
                                            if (!e.target.checked) {
                                                guestAccount.setPassword("");
                                                guestAccount.setPasswordError("");
                                            }
                                        }}
                                        password={guestAccount.password}
                                        onPasswordChange={guestAccount.setPassword}
                                        passwordError={guestAccount.passwordError}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Payment Method — only show selector for guest path with multiple methods */}
                        {!isAuthenticated && eventData.paymentMethods.length > 1 && (
                            <div className="payment-method-card">
                                <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--dl-color-purple-default)'}}>Payment Method</h3>
                                <div>
                                    {eventData.paymentMethods.map(method => (
                                        <div key={method} className="payment-option">
                                            <CustomFormInput
                                                type="radio"
                                                variant="purple"
                                                layout="standard"
                                                name="paymentMethod"
                                                value={method}
                                                label={method.charAt(0).toUpperCase() + method.slice(1)}
                                                checked={selectedPaymentMethod === method}
                                                onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Purchase Summary */}
                        <div className="purchase-summary-card">
                            <h3 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--dl-color-purple-default)'}}>Purchase Summary</h3>
                            <div>
                                {Object.entries(ticketSelections).map(([ticketType, quantity]) => {
                                    if (quantity > 0) {
                                        const ticketInfo = eventData.ticketTypes.find(t => t.name === ticketType);
                                        return (
                                            <div key={ticketType} className="summary-row">
                                                <span>{ticketType} x {quantity}</span>
                                                <span>{formatCurrencyFromMinor(ticketInfo.price * quantity, ticketInfo.currency)}</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                                {displayFees && (displayFees.platformFee > 0 || displayFees.processingFee > 0) && (
                                    <div className="summary-row">
                                        <ServiceFeeInfo />
                                        <span>{formatCurrencyFromMinor(displayFees.platformFee + displayFees.processingFee, displayFees.currency)}</span>
                                    </div>
                                )}
                                <div className="summary-total">
                                    <span>Total</span>
                                    <span>
                                        {displayFees
                                            ? formatCurrencyFromMinor(displayFees.totalPrice, displayFees.currency)
                                            : isQuoting
                                                ? "Calculating..."
                                                : formatCurrencyFromMinor(totalAmount)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Supplier policy acknowledgement gate — reuses the event's existing
                            refund policy + terms text (no new ticket storage). Always rendered so
                            the disclosed-agent wording shows and the buyer must agree before paying.
                            Wrapped with the summary card's horizontal inset (1.5rem) so the gate aligns
                            with the Purchase Summary content rather than running to the container edge. */}
                        <div style={{ padding: "0 1.5rem" }}>
                            <SupplierPolicyAcknowledgement
                                variant="modal-link"
                                sections={[
                                    { label: "Refund Policy", text: eventData.refundPolicy || "" },
                                    { label: "Terms and Conditions", text: eventData.termsAndConditions || "" },
                                ]}
                                supplierName={orgName}
                                surfaceLabel="ticket"
                                acknowledged={policyAck}
                                onAcknowledgedChange={setPolicyAck}
                            />
                        </div>

                        {/* Purchase Buttons / Stripe Elements */}
                        {isDemo ? (
                            <div className="ticket-payment-block" style={{ padding: '24px 24px 0 24px' }}>
                                <div style={{ border: '2px dashed var(--dl-color-lightplus)', borderRadius: '8px', padding: '24px', textAlign: 'center', color: 'var(--dl-color-text-muted)', background: 'var(--dl-color-gray-50)', marginBottom: '12px' }}>
                                    <p style={{ margin: 0, fontWeight: 600 }}>Stripe Payment Form</p>
                                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem' }}>Card / Apple Pay / Google Pay would appear here</p>
                                </div>
                                <button className="purchase-button" disabled style={{ width: '100%' }}>
                                    Purchase ({totalAmount > 0 ? formatCurrencyFromMinor(totalAmount) : 'select tickets above'})
                                </button>
                            </div>
                        ) : showStripeForm && stripeClientSecret ? (
                            <div className="ticket-payment-block" style={{ padding: '24px' }}>
                                <Elements stripe={getStripePromise()} options={{
                                    clientSecret: stripeClientSecret,
                                    appearance: {
                                        theme: "stripe",
                                        variables: {
                                            colorPrimary: brandingData?.buttonColor || brandingData?.headerColor || "#451f78",
                                            colorBackground: "#ffffff",
                                            colorText: "#1a1a1a",
                                            borderRadius: "8px",
                                            fontFamily: "system-ui, sans-serif",
                                        },
                                    },
                                }}>
                                    <TicketPaymentForm
                                        transactionId={stripeTransactionId}
                                        onSuccess={handleStripeSuccess}
                                        policyAck={policyAck}
                                        payLabel={displayFees ? `Pay ${formatCurrencyFromMinor(displayFees.totalPrice, displayFees.currency)}` : undefined}
                                    />
                                </Elements>
                            </div>
                        ) : (
                            <div className="purchase-buttons" style={{ padding: '24px' }}>
                                {/* On mobile the in-app browser's native Done/✓ closes + returns to the app.
                                    The in-page Cancel can't dismiss that browser (its dancelocale:// deep link
                                    is blocked inside iOS's in-app browser), so hide it rather than show a dead button. */}
                                {returnApp !== "mobile" && (
                                    <button
                                        onClick={handleCancel}
                                        className="cancel-button"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button
                                    onClick={handlePurchase}
                                    disabled={totalAmount <= 0 || isQuoting || !quote || isProcessing || isCreatingPaymentSession || (!isAuthenticated && !selectedPaymentMethod) || !policyAck || (!isAuthenticated && emailHasAccount)}
                                    className="purchase-button"
                                >
                                    {isCreatingPaymentSession
                                        ? "Preparing checkout..."
                                        : isProcessing
                                            ? "Processing..."
                                            : quote
                                                ? `Purchase (${formatCurrencyFromMinor(quote.totalPrice, quote.currency)})`
                                                : "Purchase"}
                                </button>
                            </div>
                        )}

                        {(purchaseError || quoteError) && (
                            <div className="error-message">
                                <p>{purchaseError || quoteError}</p>
                            </div>
                        )}

                        {/* Cancel link — hidden on mobile (the in-app browser's native ✓ dismisses + returns;
                            the in-page deep-link Cancel is a no-op inside iOS's in-app browser). */}
                        {returnApp !== "mobile" && (
                            <div style={{ textAlign: 'right', marginTop: '16px', paddingTop: '12px', paddingLeft: '24px', paddingRight: '24px', paddingBottom: '12px', borderTop: '1px solid var(--dl-color-border)' }}>
                                <button
                                    onClick={handleCancel}
                                    style={{ background: 'none', border: 'none', color: 'var(--dl-color-gray-400)', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0' }}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>
            </div>{/* end ticket-purchase-body */}

            {/* Guest Login Modal — shown when guest email matches an existing account */}
            <GuestLoginModal
                isOpen={showGuestLoginModal}
                onClose={() => setShowGuestLoginModal(false)}
                prefillEmail={guestEmail}
                onLoginSuccess={() => {
                    setShowGuestLoginModal(false);
                    setPurchaseError("");
                }}
            />

            {/* Web vs App choice — shown to guest users on first visit */}
            <BookingOptionModal
                isOpen={showPurchaseOptionModal}
                onClose={() => { setShowPurchaseOptionModal(false); setHasChosenWebPurchase(true); }}
                onOpenApp={handleOpenInApp}
                onContinueWeb={handleContinueOnWeb}
                heading="How would you like to purchase?"
                appLabel="Open in Dance Localé App"
                appSub="Ticket wallet & event management"
                webLabel="Buy on Website"
                webSub="No download needed"
            />

            {/* Desktop download banner — shown when user chose "app" on desktop */}
            {showDownloadBanner && (
                <div className="ticket-download-banner">
                    <div className="ticket-download-banner-content">
                        <p className="ticket-download-heading">Get the Dance Localé App</p>
                        <p className="ticket-download-body">
                            Download the app, create an account, and search for this event to purchase tickets with your ticket wallet.
                        </p>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <a href="https://apps.apple.com/au/app/dance-local%C3%A9/id6748289759" target="_blank" rel="noopener noreferrer" className="ticket-download-link">
                                App Store
                            </a>
                            <a href="https://play.google.com/store/apps/details?id=com.yadawn.dancelocale" target="_blank" rel="noopener noreferrer" className="ticket-download-link">
                                Google Play
                            </a>
                        </div>
                        <button
                            onClick={() => {
                                setShowDownloadBanner(false);
                                setHasChosenWebPurchase(true);
                                if (pendingPurchaseEventId) {
                                    startPurchaseFlow(pendingPurchaseEventId);
                                    setPendingPurchaseEventId(null);
                                }
                            }}
                            className="ticket-download-dismiss"
                        >
                            Continue on website instead
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketPurchase;